import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import { Component, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatButtonModule, MatIconButton } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCommonModule, MatRippleModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource, MatTableModule } from '@angular/material/table';
import { RouterModule } from '@angular/router';
import { AppRolesDirective } from '@directives/appRoles/app-roles.directive';
import { CantidadPorEstadoDto } from '@models/base/cantidadPorEstadoDto.mode';
import { EstadosTareas } from '@models/base/identificadores.model';
import { TareaDto } from '@models/tarea/tareaDto.model';
import { TareaFilter } from '@models/tarea/tareaFilter.model';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { TareaService } from '@services/tarea/tarea.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { Chart, ChartConfiguration, ChartType, registerables } from 'chart.js';

// Registrar Chart.js components
Chart.register(...registerables);

@Component({
  standalone: true,
  imports: [
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatDividerModule,
    MatButtonModule,
    MatIconButton,
    MatIconModule,
    MatMenuModule,
    RouterModule,
    MatTableModule,
    NgxSpinnerModule,
    CommonModule,
    AppRolesDirective,
    MatCommonModule,
    MatRippleModule,
    MatInputModule
  ],
  selector: 'app-dashboard-tareas',
  templateUrl: './dashboard-tareas.component.html',
  styleUrls: ['./dashboard-tareas.component.css']
})
export class DashboardTareasComponent implements AfterViewInit, OnDestroy {

  @ViewChild('pieChart', { static: false }) pieChart!: ElementRef<HTMLCanvasElement>;

  data = false
  displayedColumns: string[] = [];
  dataSource = new MatTableDataSource<TareaDto>();
  cardLayout: any

  formBuilder: FormBuilder = new FormBuilder;
  form!: FormGroup;
  cantidadPorEstadoDto: CantidadPorEstadoDto[] = [];

  private chart: Chart | null = null;
  private chartInitialized = false;
  private dataLoaded = false;

  constructor(
    private tareaService: TareaService,
    private snackBar: SnackBarService,
    private spinner: NgxSpinnerService,
    private breakpointObserver: BreakpointObserver,
  ) {
    this.breakpointObserver.observe(['(max-width: 600px)']).subscribe(result => {
      this.displayedColumns = result.matches ?
        ["titulo", "prioridad", "accion"] :
        ["titulo", "expediente", "fechaAlta", "prioridad", "accion"];
    });
  }

  @ViewChild(MatTable, { static: true }) table!: MatTable<any>;
  private paginator!: MatPaginator;
  private sort!: MatSort;

  @ViewChild(MatSort) set matSort(ms: MatSort) {
    this.sort = ms;
    if (this.dataSource && this.sort) {
      this.dataSource.sort = this.sort;
    }
  }

  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    if (this.dataSource && this.paginator) {
      this.dataSource.paginator = this.paginator;
    }
  }

  ngAfterViewInit(): void {
    // Esperar un poco más para asegurar que el DOM esté completamente renderizado
    setTimeout(() => {
      this.tryInitChart();
    }, 1500);
  }

  ngOnInit(): void {
    this.getCantidadPorEstadoDto()
    let tareaFilter = this.setTareaFilter()
    this.getAllTareaFilter(tareaFilter)
  }

  tryInitChart(): void {
    // Solo inicializar si tenemos datos y el ViewChild está disponible
    if (this.cantidadPorEstadoDto.length > 0 && this.pieChart?.nativeElement) {
      this.initChart();
    } else {
      console.log('Condiciones no cumplidas para inicializar:');
      console.log('- Datos disponibles:', this.cantidadPorEstadoDto.length > 0);
      console.log('- Canvas disponible:', !!this.pieChart?.nativeElement);
    }
  }

  initChart(): void {

    if (this.chartInitialized) {
      this.updateChart();
      return;
    }

    const canvas = this.pieChart?.nativeElement;
    if (!canvas) {
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }

    console.log('Creando gráfico con Chart.js...');

    // Destruir gráfico anterior si existe
    if (this.chart) {
      this.chart.destroy();
    }

    this.chart = new Chart(ctx, {
      type: 'pie',
      data: {
        labels: this.cantidadPorEstadoDto.map(item => item.descripcion),
        datasets: [{
          data: this.cantidadPorEstadoDto.map(item => item.cantidad),
          backgroundColor: [
            '#FF6B6B',
            '#4ECDC4',
            '#45B7D1',
            '#96CEB4',
            '#FFEAA7',
            '#DDA0DD'
          ],
          borderWidth: 2,
          borderColor: '#ffffff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'right',
            labels: {
              usePointStyle: true,
              padding: 15,
              font: {
                size: 12
              }
            }
          },
          tooltip: {
            callbacks: {
              label: (context: any) => {
                const label = context.label || '';
                const value = context.parsed;
                const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
                return `${label}: ${value} (${percentage}%)`;
              }
            }
          }
        }
      }
    });

    this.chartInitialized = true;
  }

  updateChart(): void {
    if (!this.chart) {
      console.log('Gráfico no existe, reintentando inicialización...');
      this.chartInitialized = false;
      setTimeout(() => this.tryInitChart(), 500);
      return;
    }

    this.chart.data.labels = this.cantidadPorEstadoDto.map(item => item.descripcion);
    this.chart.data.datasets[0].data = this.cantidadPorEstadoDto.map(item => item.cantidad);
    this.chart.update();

    console.log('Gráfico actualizado exitosamente');
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  setTareaFilter() {
    let tareaFilter = new TareaFilter()
    return tareaFilter;
  }

  getCantidadPorEstadoDto() {
    this.spinner.show("spCantidadPorEstado")
    this.tareaService.getCantidadPorEstadoDto().subscribe(
      data => {
        this.spinner.hide("spCantidadPorEstado")
        this.cantidadPorEstadoDto = data
        this.dataLoaded = true;

        // Intentar crear/actualizar el gráfico después de recibir los datos
        setTimeout(() => {
          this.tryInitChart();
        }, 200);
      },
      error => {
        this.spinner.hide("spCantidadPorEstado")
        this.snackBar.showError(error, "Error");
      })
  }

  calcularCantidadTotalPorEstado(): number {
    let cantidadTotal = 0;
    for (const item of this.cantidadPorEstadoDto) {
      cantidadTotal += item.cantidad;
    }
    return cantidadTotal;
  }

  getAllTareaFilter(tareaFilter: TareaFilter) {
    this.spinner.show('spListado')
    this.tareaService.getAllByFilter(tareaFilter)
      .subscribe(
        data => {
          this.spinner.hide('spListado')
          this.dataSource.data = data
          this.dataSource.sort = this.sort;
          this.dataSource.paginator = this.paginator;
          this.data = this.dataSource.data.length >= 1 ? true : false
        },
        error => {
          this.spinner.hide('spListado')
          this.snackBar.showError(error, "Error");
        })
  }

  getPrioridadTexto(prioridad: number): string {
    switch(prioridad) {
      case 1: return 'Baja';
      case 2: return 'Media';
      case 3: return 'Alta';
      default: return '';
    }
  }

  getPrioridadColor(prioridad: number): string {
    switch(prioridad) {
      case 1: return 'success';
      case 2: return 'warn';
      case 3: return 'accent';
      default: return 'primary';
    }
  }

  // MÉTODO DEBUG TEMPORAL
  forceCreateChart(): void {
    this.chartInitialized = false;
    this.chart = null;

    setTimeout(() => {
      this.tryInitChart();
    }, 100);
  }

  ngOnDestroy(): void {
    if (this.chart) {
      this.chart.destroy();
    }
  }
}
